import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import {
  CheckCircle2, ChevronRight, ArrowRight, Flame, DoorOpen, Wind,
  ShieldAlert, AlertTriangle, Workflow, Eye,
} from "lucide-react";
import usePageview, { getVisitorSession } from "@/lib/usePageview";
import useNoIndex from "@/lib/useNoIndex";
import BespokePageRibbon from "@/components/BespokePageRibbon";
import BespokeWatermark from "@/components/BespokeWatermark";

// Phase 25.16ai — Premier Compliance UK bespoke proposal page.
//
// Trigger: Long-time personal contact between founder and Zach Poole,
// CEO of Premier Compliance (UK fire-safety specialists, Deeside,
// Flintshire · ~50 staff · ASX-style growth phase). Page is the
// bespoke ABM landing the warm-channel intro will point to.
//
// Strategic frame: Zach gave Manuel his first job in Australia
// years back. This page is a personal note, not a commercial pitch.
// No money. No revenue share. No partnership terms. The ask is
// small: let your compliance manager and one director try DARA for
// a week. Give an honest read. That's it.
//
// If anything lands, partnership conversation comes LATER —
// when Zach asks for it, not when DARA proposes it.
//
// Same architectural pattern as /dabble, /ventia. Brand acknowledgment
// via navy/orange accents (Premier's palette) layered over DARA's
// Bloomberg-terminal black. Logo-free text mark to dodge trademark.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Four specialty blocks, mapped 1:1 to Premier's LinkedIn list:
// passive fire protection → fire doors → fire dampers → ductwork.
// Each block exists to show Zach that Manuel did the homework on
// Premier's actual world — not to sell a feature. Tone is honest,
// curious, "here's what I think your team would want, tell me where
// I've got it wrong." Zero commercial language.
const CAPABILITIES = [
  {
    Icon: Flame,
    code: "01",
    name: "Passive Fire Protection · what DARA tracks",
    plain: "Every fire-stopping inspection your team does today lives in a PDF that gets filed and rarely reopened. DARA reads those reports and keeps a living register — per-building, per-penetration, with photo evidence and revalidation cadence. So when your compliance manager wants to know which buildings have outstanding remedials this week, the answer is one screen away. Not a phone call to whoever ran the last inspection.",
    builtIn: "Per-building compartmentation map · penetration register with photo trail · automated revalidation cadence · BSR-ready audit pack · remedials auto-flagged when overdue.",
    honest: "I built this around what I think the Building Safety Regulator now wants to see continuously. You'll know better than me where I've under-specified or where I've over-engineered for Premier's actual rhythm.",
  },
  {
    Icon: DoorOpen,
    code: "02",
    name: "Fire Doors · BS 8214 / BS 9999 history",
    plain: "Every fire door your team inspects gets a permanent identity inside DARA — location, hardware spec, last inspection, defect history, photo trail. The compliance manager stops asking 'when was door C-12 last checked?' because the answer is one click. The directors see the portfolio-wide picture: which clients have most overdue doors, where the risk is concentrating.",
    builtIn: "Door-level inspection history · defect remediation tracking · hardware change chain of custody · BS 8214 / BS 9999 evidence chain · client-level risk concentration view.",
    honest: "I followed BS 8214 / BS 9999 as the scaffold but I haven't sat across from a UK Head of Compliance on this. Tell me what's missing from this view — what does your compliance manager actually need to see on a Monday?",
  },
  {
    Icon: Wind,
    code: "03",
    name: "Fire Dampers · BSEN 15004 drop-test ledger",
    plain: "Every damper your team drop-tests becomes a permanent record — date, result, technician, photo, next-cadence. When an insurer or HSE inspector asks 'when did the kitchen extract damper last drop-test?' the compliance manager doesn't dig through emails. The answer is in DARA. Across the portfolio, DARA also flags failure-mode patterns — if the same damper model is failing across 3 clients, that's surfaced.",
    builtIn: "Per-damper drop-test ledger · pass/fail with photo evidence · cadence automation · BSEN 15004 / BS 9999 evidence chain · failure-mode pattern detection across portfolio.",
    honest: "The pattern-detection layer is the part I'm least sure about. It works architecturally but I want to know if it's noise or signal for someone running compliance day-to-day at your scale.",
  },
  {
    Icon: Workflow,
    code: "04",
    name: "Vent &amp; Ductwork Hygiene · TR/19 record",
    plain: "Every ductwork clean your team performs gets logged with before/after evidence, TR/19 grade tracking, and automatic re-clean cadence by system category. The compliance manager sees the next-clean queue. The directors see which clients are due for renewal conversations because their hygiene posture is strong (a sales lead) or weak (a retention risk).",
    builtIn: "TR/19 grade tracking · per-system hygiene history · before/after photo audit · re-clean cadence by category (kitchen extract 3-6mo, general HVAC 12-24mo) · renewal-pressure signal.",
    honest: "I've heard TR/19 non-compliance is a top reason insurance claims get denied — but that's secondhand. If your team has seen patterns I haven't captured here, I'd love to know.",
  },
];

export default function PremierCompliance() {
  useNoIndex();
  usePageview();

  const [form, setForm] = useState({
    email: "",
    name: "Zach Poole",
    role: "Founder & CEO",
    focus: "Let my compliance manager + one director try DARA for a week",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email) {
      toast.error("Work email is required");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/industry-leads`, {
        email: form.email,
        name: form.name,
        company: "Premier Compliance",
        role: form.role,
        message: [
          form.focus ? `Focus: ${form.focus}` : "",
          form.message ? `Notes: ${form.message}` : "",
        ].filter(Boolean).join("\n"),
        industry: "premier-compliance",
        source: "premier-bespoke",
        visitor_session: getVisitorSession(),
      });
      setSubmitted(true);
      toast.success("Received — DARA founder will reach out within one business day.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed — try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="premier-page">
      <BespokePageRibbon prospectName="Premier Compliance · UK Fire Safety" />
      <BespokeWatermark pageLabel="PREMIER" position="top" />

      {/* Top nav */}
      <nav className="sticky top-0 z-50 border-b border-zinc-900 bg-black/85 backdrop-blur">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group" data-testid="premier-back-to-home">
            <span className="text-[#FFB000] font-mono text-sm">{`>_`}</span>
            <span className="font-heading font-black tracking-tight text-white text-base">DARA</span>
            <span className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-zinc-500 ml-2">
              · A NOTE FOR ZACH
            </span>
          </Link>
          <div className="flex items-center gap-3 sm:gap-5">
            <a href="#engage" data-testid="premier-nav-engage" className="bg-[#FFB000] text-black font-bold font-mono text-[11px] sm:text-[10px] uppercase tracking-widest px-3 py-2 hover:opacity-90">
              Write back
            </a>
          </div>
        </div>
      </nav>

      {/* HERO — personal backstory + the ask */}
      <section className="border-b border-zinc-900 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#10283F]/30 via-transparent to-[#FFB000]/5 pointer-events-none" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-20 relative">
          <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-[0.25em] text-[#FFB000] mb-4">
            // A NOTE FOR ZACH POOLE · FROM MANUEL · MAY 2026
          </div>
          <div className="flex items-center gap-3 mb-4">
            <PremierRingMark />
            <div className="font-mono text-[11px] sm:text-[10px] sm:text-xs uppercase tracking-widest text-zinc-400">
              FOR PREMIER COMPLIANCE · UK
            </div>
          </div>
          <h1 className="font-heading font-black text-3xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
            Zach — you gave me my first job in Australia. <span className="text-[#FFB000]">I'd love your honest read on this.</span>
          </h1>
          <p className="mt-6 text-base sm:text-lg text-zinc-300 max-w-3xl leading-relaxed">
            You probably don't remember the details, but I do. You took a chance on me as a labour construction worker when no one else would. Years on, I've been building something I'd genuinely like you to look at. Not as a customer. As a sage. As someone who's been ahead of me in the journey and who knows the UK market in ways I don't.
          </p>
          <p className="mt-4 text-base sm:text-lg text-zinc-300 max-w-3xl leading-relaxed">
            What I built is called DARA. Plain English version: it sits above the inspection tools your team already uses, and produces two daily briefs — one for the directors, one for the compliance manager — so the people who need to see compliance posture at a glance can. It doesn't replace anything. It doesn't ask you to rip out what works. It just makes the evidence your team is already producing legible to the people inside Premier who need to act on it.
          </p>
          <p className="mt-4 text-base sm:text-lg text-zinc-300 max-w-3xl leading-relaxed">
            <b>The ask is small.</b> Let your compliance manager and one director try DARA for a week. Tell me what's broken. Tell me what's missing. Tell me where I've misread the UK fire-safety market. That's it. No money, no contract, no commitment. Just a read from someone whose judgement I trust.
          </p>

          <div className="mt-10 flex items-center gap-4 flex-wrap">
            <a
              href="#engage"
              data-testid="premier-hero-engage-cta"
              className="inline-flex items-center gap-2 bg-[#FFB000] text-black font-bold font-mono text-xs uppercase tracking-widest px-6 py-3 hover:opacity-90"
            >
              <ChevronRight className="w-4 h-4" /> Write back to Manuel <ArrowRight className="w-4 h-4" />
            </a>
            <a
              href="#briefs"
              data-testid="premier-hero-briefs-link"
              className="font-mono text-[11px] uppercase tracking-widest text-zinc-300 hover:text-[#FFB000] underline-offset-4 hover:underline flex items-center gap-1.5"
            >
              <Eye className="w-3.5 h-3.5" /> See the two briefs first
            </a>
          </div>
        </div>
      </section>

      {/* THE SETUP — UK context, softer tone */}
      <section className="border-b border-zinc-900 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-3">
            // SOME CONTEXT · WHY I THINK THIS COULD HELP
          </div>
          <h2 className="font-heading font-black text-2xl sm:text-4xl tracking-tight max-w-5xl leading-tight">
            The Building Safety Act 2022 changed what regulators want to see — <span className="text-[#FFB000]">continuous evidence</span>, not annual certificates.
          </h2>
          <p className="mt-6 text-sm sm:text-base text-zinc-300 max-w-4xl leading-relaxed">
            You'll know this better than me, but from where I'm sitting in Australia, the shift in the UK is striking. Premier's team already does the work — passive fire, doors, dampers, ductwork. The challenge isn't the inspection. It's that everyone above the field crew — your directors, your compliance manager, the duty-holder you serve — has to make decisions off PDFs and emails that don't talk to each other.
          </p>
          <p className="mt-4 text-sm sm:text-base text-zinc-300 max-w-4xl leading-relaxed">
            I wanted to see if DARA could close that gap for someone like Premier. The version I built for you to look at sits inside Premier — not in front of your clients — and gives the people running the business a clear daily picture of where you're exposed and where you're solid.
          </p>
        </div>
      </section>

      {/* TWO BRIEFS · directors' + compliance manager's */}
      <section id="briefs" className="border-b border-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-3">
            // TWO BRIEFS · TWO SEATS AT THE PREMIER TABLE
          </div>
          <h2 className="font-heading font-black text-2xl sm:text-4xl tracking-tight max-w-5xl leading-tight">
            One brief for you and the directors. One brief for your compliance manager. <span className="text-[#FFB000]">Same data underneath, different lens.</span>
          </h2>
          <p className="mt-3 text-sm text-zinc-400 max-w-3xl leading-relaxed">
            This is the part I'd love you to try with your team. Two morning briefs, produced from the inspection data Premier already collects. One sized for a board-level conversation. One sized for the operational hand on the wheel.
          </p>

          <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-px bg-zinc-900 border border-zinc-900" data-testid="premier-briefs-grid">

            {/* Directors' brief */}
            <div className="bg-black p-6 sm:p-8 flex flex-col" data-testid="premier-brief-directors">
              <div className="flex items-center gap-3 mb-3">
                <ShieldAlert className="w-5 h-5 text-[#FFB000]" />
                <span className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000]">// THE DIRECTORS' BRIEF</span>
              </div>
              <h3 className="font-heading font-bold text-xl tracking-tight mb-3">
                What you, Zach, and the board see on a Monday morning.
              </h3>
              <p className="text-sm text-zinc-300 leading-relaxed mb-4">
                A single page. Top of page: where Premier stands across every client and every building this week. Below: the three risks that need a board-level call this week. Below that: the wins to celebrate — clients whose compliance posture has visibly improved because of work Premier did.
              </p>
              <div className="border-t border-zinc-900 pt-4 space-y-2.5">
                <BriefLine label="Portfolio posture" body="42 buildings across 9 clients · 38 green · 3 amber · 1 red flagged for board attention" />
                <BriefLine label="This week's exposures" body="St. Mary's NHS Trust · damper drop-test overdue 14 days · insurer notification due Friday" />
                <BriefLine label="Client wins" body="Acme FM · BSR audit passed · cited Premier's inspection chain as exemplar" />
                <BriefLine label="Pipeline pressure" body="6 clients renewing within 90 days · 2 at risk · 4 expandable to higher-tier contract" />
              </div>
              <p className="mt-5 text-xs sm:text-[11px] text-zinc-500 leading-relaxed italic">
                Built so a director can read it in 60 seconds before the standup and walk in knowing the three things that need a Premier-level response this week.
              </p>
            </div>

            {/* Compliance Manager's brief */}
            <div className="bg-black p-6 sm:p-8 flex flex-col" data-testid="premier-brief-compliance">
              <div className="flex items-center gap-3 mb-3">
                <Eye className="w-5 h-5 text-[#FFB000]" />
                <span className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000]">// THE COMPLIANCE MANAGER'S BRIEF</span>
              </div>
              <h3 className="font-heading font-bold text-xl tracking-tight mb-3">
                What your head of compliance sees the same morning.
              </h3>
              <p className="text-sm text-zinc-300 leading-relaxed mb-4">
                Same source data, different surface. Operational rather than strategic. Per-building drilldown. Job-pack-ready for the field crew. Every overdue retest, every failed damper drop, every remedial that's been signed off — surfaced so the people running the day-to-day know exactly where to point the team next.
              </p>
              <div className="border-t border-zinc-900 pt-4 space-y-2.5">
                <BriefLine label="Today's job stack" body="11 inspections scheduled · 3 overdue retests routed to field team · 2 remedials awaiting client sign-off" />
                <BriefLine label="Failure-mode aggregation" body="Kitchen extract dampers · 4 fails in 6 weeks across 2 clients · pattern flagged for review" />
                <BriefLine label="Evidence gaps" body="Acme FM building 3 · TR/19 photographic evidence missing from last clean · technician notified" />
                <BriefLine label="Insurer & BSR queries" body="3 client queries logged this week · all responded within 48hr · 1 escalated to director brief" />
              </div>
              <p className="mt-5 text-xs sm:text-[11px] text-zinc-500 leading-relaxed italic">
                Built so the operational hand on the wheel never has a Monday where they're guessing what the team needs to focus on this week.
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* SPECIALTIES · what DARA understands about your patch */}
      <section id="capabilities" className="border-b border-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-3">// FOUR SPECIALTIES · WHAT DARA UNDERSTANDS ABOUT YOUR PATCH</div>
          <h2 className="font-heading font-black text-2xl sm:text-4xl tracking-tight">
            I tried to learn Premier's world before writing a line of code.
          </h2>
          <p className="mt-3 text-sm text-zinc-400 max-w-3xl leading-relaxed">
            These four blocks show what DARA already tracks for each part of your service — passive fire, doors, dampers, ductwork. They're not a feature list. They're me showing my workings. If anything here looks wrong or naive about how your team actually works, that's the most useful thing you could tell me.
          </p>

          <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-px bg-zinc-900 border border-zinc-900" data-testid="premier-capabilities-grid">
            {CAPABILITIES.map(({ Icon, code, name, plain, builtIn, honest }) => (
              <div key={code} className="bg-black p-6 sm:p-7 flex flex-col" data-testid={`premier-cap-${code}`}>
                <div className="flex items-center gap-3 mb-3">
                  <Icon className="w-5 h-5 text-[#FFB000]" />
                  <span className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-zinc-500">// SPECIALTY {code}</span>
                </div>
                <h3 className="font-heading font-bold text-base sm:text-lg tracking-tight mb-3" dangerouslySetInnerHTML={{ __html: name }} />
                <p className="text-xs sm:text-sm text-zinc-300 leading-relaxed mb-4">{plain}</p>
                <div className="mt-auto pt-3 border-t border-zinc-900 space-y-2">
                  <div>
                    <div className="font-mono text-[11px] sm:text-[10px] sm:text-[9px] uppercase tracking-widest text-[#FFB000] mb-1">// WHAT DARA TRACKS TODAY</div>
                    <p className="text-xs sm:text-[11px] text-zinc-400 leading-relaxed">{builtIn}</p>
                  </div>
                  <div>
                    <div className="font-mono text-[11px] sm:text-[10px] sm:text-[9px] uppercase tracking-widest text-[#FFB000] mb-1">// WHERE I'M LESS SURE</div>
                    <p className="text-xs sm:text-[11px] text-zinc-300 leading-relaxed italic">{honest}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* THE ASK · just a read, no commitment */}
      <section className="border-b border-zinc-900 bg-[#0a0a0a]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-3">
            // THE ASK · SMALL, HONEST, NO COMMITMENT
          </div>
          <h2 className="font-heading font-black text-2xl sm:text-4xl tracking-tight leading-tight">
            Let your compliance manager and one director try DARA for a week. <span className="text-[#FFB000]">Then tell me the truth.</span>
          </h2>
          <p className="mt-4 text-sm sm:text-base text-zinc-300 leading-relaxed">
            That's the whole ask. No money. No contract. No replacement of anything Premier already uses. I'll set up access for two people on your team, walk them through the directors' brief and the compliance manager's brief, and step away. After a week, you and your team tell me where I'm right, where I'm wrong, and where I'm wasting your time.
          </p>
          <p className="mt-4 text-sm sm:text-base text-zinc-300 leading-relaxed">
            If it lands, we'll find a way to keep going — maybe Premier helps me get DARA's foot in the UK door with a single client, maybe nothing. <b>That conversation is for later, not now.</b> Right now I just want a read from someone who knows the UK market and whose judgement I trust.
          </p>
          <p className="mt-4 text-sm sm:text-base text-zinc-300 leading-relaxed">
            I'm asking you because you gave me a chance once. Now I'm hoping you'll give DARA a chance to be looked at.
          </p>
        </div>
      </section>

      {/* ENGAGE FORM */}
      <section id="engage" className="border-b border-zinc-900">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-3">
            // WRITE BACK · DIRECT TO MANUEL
          </div>
          <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tight">
            One line back is enough. Even just "yes worth a look" or "not the right time."
          </h2>
          <p className="mt-3 text-sm text-zinc-400 leading-relaxed">
            Drop a note here or text me on the number you still have. If it's a yes, I'll set up access for two people on your team within 48 hours and we go from there.
          </p>

          {submitted ? (
            <div className="mt-8 border border-[#FFB000]/40 bg-[#FFB000]/5 p-6" data-testid="premier-form-success">
              <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-2 flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" /> GOT IT
              </div>
              <p className="text-sm text-zinc-200 leading-relaxed">
                Cheers Zach. I'll be in touch within the day. If you want to think about who from your team would be best placed to give it a week — your compliance manager and one director — that'd help me get them set up faster.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-8 space-y-5 border border-zinc-900 bg-black p-6 sm:p-8" data-testid="premier-engage-form">
              <Input label="Your email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} type="email" required testid="premier-form-email" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <Input label="Your name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="premier-form-name" />
                <Input label="Your role" value={form.role} onChange={(v) => setForm({ ...form, role: v })} testid="premier-form-role" />
              </div>
              <Input label="What you'd want to try first (editable)" value={form.focus} onChange={(v) => setForm({ ...form, focus: v })} testid="premier-form-focus" />
              <label className="block">
                <span className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-zinc-500">Anything you want to say first (optional)</span>
                <textarea
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  data-testid="premier-form-message"
                  rows={4}
                  className="mt-1 w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-xs text-white focus:outline-none focus:border-[#FFB000]"
                />
              </label>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <p className="text-[11px] sm:text-[10px] text-zinc-600 font-mono leading-relaxed">
                  Personal note · prepared for Zach.<br />Not for redistribution outside Premier without permission.
                </p>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="premier-form-submit"
                  className="bg-[#FFB000] text-black font-bold font-mono text-xs uppercase tracking-widest px-6 py-3 hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <ChevronRight className="w-4 h-4" />
                  {submitting ? "Sending..." : "Send to Manuel"}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>

      {/* RISK / HONESTY BLOCK */}
      <section className="border-b border-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
          <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-3 flex items-center gap-2">
            <AlertTriangle className="w-3.5 h-3.5" />// HONESTLY · A FEW THINGS YOU SHOULD KNOW
          </div>
          <ul className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <RiskItem text="DARA is pre-seed and built in Australia. UK fire-safety is new ground for me. That's part of why I'm asking your team to look — your read tells me where I've made assumptions that don't hold up in your market." />
            <RiskItem text="Premier almost certainly already uses a field-service platform like Joblogic or BigChange. DARA isn't asking you to replace anything. It sits above the tools your team uses today and produces the daily briefs — nothing more." />
            <RiskItem text="If after a week your team says 'this isn't useful' or 'we've got something that does this already', that's a fine answer. The point of the ask is honest feedback. There's no commercial obligation either way." />
          </ul>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 grid grid-cols-1 sm:grid-cols-3 gap-8">
          <div>
            <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-zinc-500 mb-3">For Zach</div>
            <p className="text-xs sm:text-[11px] text-zinc-400 leading-relaxed">
              I built this page just for you. If anyone else at Premier asks where it came from, you can share it — it won't go anywhere else.
            </p>
          </div>
          <div>
            <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-zinc-500 mb-3">If you want to see DARA's broader work</div>
            <ul className="space-y-2">
              <li><Link to="/why-dara" className="text-xs sm:text-[11px] text-zinc-400 hover:text-[#FFB000]">Why DARA · the architecture story</Link></li>
              <li><Link to="/wagering" className="text-xs sm:text-[11px] text-zinc-500 hover:text-[#FFB000]">DARA Wagering · current vertical</Link></li>
            </ul>
          </div>
          <div>
            <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-zinc-500 mb-3">DARA OS</div>
            <p className="text-xs sm:text-[11px] text-zinc-600 leading-relaxed">
              Founder-led intelligence platform. Built in Australia. Hoping to learn UK fire-safety with help from someone who knows the patch.
            </p>
            <p className="text-[11px] sm:text-[10px] text-zinc-700 leading-relaxed mt-3 font-mono">
              Personal note · prepared for Zach Poole · not for redistribution outside Premier without permission.
            </p>
          </div>
        </div>
        <div className="border-t border-zinc-900 px-4 sm:px-6 py-4 max-w-7xl mx-auto flex items-center justify-between">
          <span className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-zinc-600">© DARA OS · {new Date().getFullYear()}</span>
          <span className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-zinc-700">A NOTE FOR ZACH · v2</span>
        </div>
      </footer>
      <BespokeWatermark pageLabel="PREMIER" position="bottom" />
    </div>
  );
}

// Premier's logo on LinkedIn is a 4-color circular gradient ring
// (orange · yellow · green · blue) on navy. We render a stylized
// approximation as inline SVG to honor the brand without using
// their actual mark (trademark + asset-hosting hygiene).
function PremierRingMark() {
  return (
    <svg width="22" height="22" viewBox="0 0 22 22" fill="none" aria-label="Premier ring mark">
      <defs>
        <linearGradient id="premier-ring" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#F58220" />
          <stop offset="33%" stopColor="#F8C419" />
          <stop offset="66%" stopColor="#7DBE3C" />
          <stop offset="100%" stopColor="#2EA3D9" />
        </linearGradient>
      </defs>
      <circle cx="11" cy="11" r="8.5" stroke="url(#premier-ring)" strokeWidth="3" fill="none" />
    </svg>
  );
}

function BriefLine({ label, body }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-baseline sm:gap-3">
      <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] whitespace-nowrap sm:w-44 flex-shrink-0">
        // {label}
      </div>
      <p className="text-[12px] text-zinc-300 leading-relaxed">{body}</p>
    </div>
  );
}

function RiskItem({ text }) {
  return (
    <li className="border border-zinc-900 p-4 text-xs text-zinc-400 leading-relaxed">{text}</li>
  );
}

function Input({ label, value, onChange, type = "text", required = false, testid }) {
  return (
    <label className="block">
      <span className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-zinc-500">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        data-testid={testid}
        className="mt-1 w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-xs text-white focus:outline-none focus:border-[#FFB000]"
      />
    </label>
  );
}
